import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Eyebrow } from "./Reveal";
import { imgSrc } from "../lib/api";

export const Gallery = ({ concert: c }) => {
  const photos = Array.isArray(c?.gallery) ? c.gallery : [];
  const [open, setOpen] = useState(null);
  const n = photos.length;
  const step = (d) => setOpen((i) => (i + d + n) % n);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, n]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!n) return null;

  return (
    <section data-testid="event-gallery" className="mx-auto max-w-7xl px-5 pb-20 lg:px-12">
      <Eyebrow>{c.is_past ? "From the night" : "Gallery"}</Eyebrow>
      <h2 className="mt-3 font-display text-5xl font-extrabold uppercase leading-[0.9] text-white sm:text-6xl">{c.is_past ? <>You had to <span className="text-gradient">be there.</span></> : "Photos"}</h2>
      <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:gap-4">
        {photos.map((src, i) => (
          <motion.button
            key={src + i}
            data-testid={`gallery-photo-${i}`}
            onClick={() => setOpen(i)}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
            className={`group relative overflow-hidden rounded-2xl bg-[#12141C] ${i === 0 ? `${n % 2 ? "col-span-2 aspect-[16/9]" : "aspect-square"} md:col-span-2 md:row-span-2 md:aspect-auto` : "aspect-square"}`}
            aria-label={`Open photo ${i + 1} of ${n}`}
          >
            <img src={imgSrc(src)} alt={`Crowd at ${c.artist}, photo ${i + 1}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
            <span className="absolute inset-0 bg-gradient-to-t from-[#9D4EDD]/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </motion.button>
        ))}
      </div>

      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-lenis-prevent data-testid="gallery-lightbox" className="max-w-5xl border-white/10 bg-[#0B0C10] p-2 text-white sm:p-3 [&>button]:hidden">
          <DialogTitle className="sr-only">{c.artist} gallery</DialogTitle>
          <DialogDescription className="sr-only">Photo {(open ?? 0) + 1} of {n}</DialogDescription>
          {open !== null && (
            <div className="relative">
              <img src={imgSrc(photos[open])} alt={`Crowd at ${c.artist}, photo ${open + 1}`} className="max-h-[80vh] w-full rounded-xl object-contain" data-testid="lightbox-image" />
              <button aria-label="Close" data-testid="lightbox-close" onClick={() => setOpen(null)} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full bg-black/70 hover:bg-[#FF5A1E] hover:text-black"><X size={18} /></button>
              {n > 1 && (
                <>
                  <button aria-label="Previous photo" data-testid="lightbox-prev" onClick={() => step(-1)} className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-black/70 hover:bg-[#FF5A1E] hover:text-black"><ChevronLeft size={20} /></button>
                  <button aria-label="Next photo" data-testid="lightbox-next" onClick={() => step(1)} className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-black/70 hover:bg-[#FF5A1E] hover:text-black"><ChevronRight size={20} /></button>
                </>
              )}
              <p className="mt-2 text-center font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid="lightbox-counter">{open + 1} / {n}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
